import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Download, File, FilePlus2, Folder, FolderUp, Loader2, Pencil, Trash2, Upload, X,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useRepoFile, useRepoTree, downloadRepoFile } from "@/api/queries/gitContent";
import { useRepoDrafts } from "@/api/queries/repoLifecycle";
import { useDeleteRepoFile, useSaveRepoFile, useUploadRepoFile } from "@/api/mutations/gitContent";
import { LifecycleBar } from "@/components/repo-lifecycle/LifecycleBar";
import { BulkUploadDialog } from "@/components/repo-bulk/BulkUploadDialog";
import { LIVE_REF, apiRef, isLive, repoErrorMessage, writeBlockedReason } from "@/components/repo-lifecycle/lifecycleVocab";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { ManagedRepo, RepoTreeEntry } from "@/types";

// #290 (UC-1): der Repo-Content-Baustein — Lifecycle-Leiste + Browser + Editor/Upload/Delete
// für EIN Repo. Aus der Repositories-Seite extrahiert, damit Fach-Sichten (Workspace-Vorlagen,
// Plugin-Detail) DENSELBEN Baustein einbetten, statt eine zweite Content-UI zu bauen.
//
// Navigations-Zustand (path/file/ref) lebt in der ADRESSZEILE (Query-Params) — bewusst auch
// beim Einbetten: ein Entwurfs-Link („schau dir das an und gib es frei") funktioniert damit
// auf JEDER Seite, die das Panel trägt. Die Repo-Auswahl selbst gehört der einbettenden Seite
// (Query-Param, Route — ihre Sache). Beim Repo-Wechsel MUSS das Panel mit `key={repo.id}`
// gerendert werden: der Remount verwirft Editor-Draft + Commit-Notiz des alten Repos.

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Fehler → Nutzer-Meldung über die EINE Code-Tabelle (lifecycleVocab, Review-Fund #8).
function writeErrorMessage(err: unknown): string {
  return repoErrorMessage(err, "Aktion fehlgeschlagen");
}

const KIND_LABEL: Record<ManagedRepo["kind"], string> = {
  marketplace: "Marketplace",
  template: "Template",
  plugin: "Plugin", // #276 (UC-3)
  // #283: taucht in der Admin-UI nie auf (R1), der Record erzwingt den Eintrag beim Kompilieren.
  workspace: "Agent-Workspace",
  process: "Prozess", // #300 (Live-Fund): vierte governed Kategorie in der Werkbank
};

export function RepoContentPanel({ repo }: { repo: ManagedRepo }) {
  const { session } = useAuth();
  const repoId = repo.id;
  // ⚠ push statt replace: Ordner/Datei/Stand sind bewusste Schritte, keine Tipp-Eingaben —
  // der Zurück-Knopf soll einen Schritt zurückgehen, nicht die Seite verlassen.
  const [searchParams, setSearchParams] = useSearchParams();
  const dirPath = searchParams.get("path") ?? "";
  const filePath = searchParams.get("file");
  const activeRef = searchParams.get("ref") ?? LIVE_REF;

  function navigateTo(patch: { path?: string | null; file?: string | null; ref?: string | null }) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }

  // Editor-Zustand (UC-2): draft=null → Ansicht; sonst Bearbeitung (mit sha vom Laden).
  const [draft, setDraft] = useState<{ content: string; sha?: string; isNew: boolean; path: string } | null>(null);
  const [commitMsg, setCommitMsg] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const uploadInput = useRef<HTMLInputElement>(null);

  const tree = useRepoTree(repoId, dirPath, apiRef(activeRef));
  const file = useRepoFile(repoId, filePath, apiRef(activeRef));
  const save = useSaveRepoFile();
  const upload = useUploadRepoFile();
  const remove = useDeleteRepoFile();

  // #278: Warum ist Speichern gerade nicht möglich? null = es ist möglich. Die UI sagt es
  // VORHER, statt den Nutzer in einen 403 laufen zu lassen — jedes Schreib-Bedienelement
  // hängt an diesem einen Ausdruck, der Grund steht als Tooltip am gesperrten Knopf.
  const writeBlocked = writeBlockedReason(repo.lifecycle, activeRef);
  const schreibgesperrt = writeBlocked !== null;
  const schreibTitel = writeBlocked ?? undefined;

  const crumbs = useMemo(() => {
    const segs = dirPath === "" ? [] : dirPath.split("/");
    return segs.map((name, i) => ({ name, path: segs.slice(0, i + 1).join("/") }));
  }, [dirPath]);

  // Review-Fix (UI-Race): Draft NICHT per Effect auf filePath verwerfen — startNewFile
  // setzt filePath(null) + draft im selben Handler; der Draft wird EXPLIZIT in den
  // Navigations-Handlern verworfen.
  function discardDraft() {
    setDraft(null);
    setCommitMsg("");
  }

  function openEntry(e: RepoTreeEntry) {
    discardDraft();
    if (e.type === "dir") {
      navigateTo({ path: e.path, file: null });
    } else {
      navigateTo({ file: e.path });
    }
  }

  function startEdit() {
    if (!file.data || file.data.isBinary) return;
    setDraft({ content: file.data.content ?? "", sha: file.data.sha, isNew: false, path: file.data.path });
  }

  function startNewFile() {
    const name = window.prompt("Dateiname (im aktuellen Ordner):");
    if (!name?.trim()) return;
    const path = dirPath ? `${dirPath}/${name.trim()}` : name.trim();
    navigateTo({ file: null });
    setDraft({ content: "", isNew: true, path });
  }

  async function onSave() {
    if (!draft) return;
    try {
      await save.mutateAsync({
        repoId,
        path: draft.path,
        content: draft.content,
        message: commitMsg,
        sha: draft.isNew ? undefined : draft.sha,
        ref: apiRef(activeRef),
      });
      toast.success(isLive(activeRef) ? `Gespeichert: ${draft.path}` : `Im Entwurf gespeichert: ${draft.path}`);
      setDraft(null);
      setCommitMsg("");
      navigateTo({ file: draft.path });
    } catch (err) {
      toast.error(writeErrorMessage(err));
    }
  }

  async function onUploadFile(f: globalThis.File) {
    // Review-Fix: Größe VOR dem Einlesen prüfen — klare Meldung statt Server-413/400.
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß (Limit 10 MB).");
      return;
    }
    const buf = new Uint8Array(await f.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i += 0x8000) {
      bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    }
    const contentBase64 = btoa(bin);
    const path = dirPath ? `${dirPath}/${f.name}` : f.name;
    // Existiert die Datei im aktuellen Ordner schon? → sha mitgeben (ändern statt anlegen).
    const existing = (tree.data ?? []).find((e) => e.path === path && e.type === "file");
    let sha: string | undefined;
    if (existing) {
      const overwrite = window.confirm(`${f.name} existiert — überschreiben?`);
      if (!overwrite) return;
      // sha der bestehenden Datei holen (Tree trägt ihn nicht) — über den File-Endpoint.
      sha = undefined; // Server-seitig: POST ohne sha schlägt bei Existenz fehl → wir holen den sha:
      try {
        const token = session?.accessToken;
        if (token) {
          const { MANAGEMENT_URL } = await import("@/config/env");
          // ⚠ Die sha MUSS vom SELBEN Stand kommen, in den wir gleich schreiben. Ohne den
          // Ref läse man den Live-Stand und schriebe mit dessen sha in den Entwurf.
          const r = apiRef(activeRef);
          const res = await fetch(
            `${MANAGEMENT_URL}/git-content/repos/${repoId}/file?path=${encodeURIComponent(path)}` +
              (r ? `&ref=${encodeURIComponent(r)}` : ""),
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (res.ok) sha = ((await res.json()) as { sha: string }).sha;
        }
      } catch {
        /* sha bleibt undefined → Server meldet Konflikt sauber */
      }
    }
    try {
      await upload.mutateAsync({ repoId, path, contentBase64, message: commitMsg || `Upload ${f.name}`, sha, ref: apiRef(activeRef) });
      toast.success(`Hochgeladen: ${path}`);
    } catch (err) {
      toast.error(writeErrorMessage(err));
    }
  }

  async function onDelete() {
    if (!file.data) return;
    try {
      await remove.mutateAsync({
        repoId,
        path: file.data.path,
        message: commitMsg || `Delete ${file.data.name}`,
        sha: file.data.sha,
        ref: apiRef(activeRef),
      });
      toast.success(`Gelöscht: ${file.data.path}`);
      navigateTo({ file: null });
    } catch (err) {
      toast.error(writeErrorMessage(err));
    } finally {
      setConfirmDelete(false);
    }
  }

  async function onDownload(path: string, name: string) {
    if (!session?.accessToken) return;
    try {
      await downloadRepoFile(session.accessToken, repoId, path, name, apiRef(activeRef));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download fehlgeschlagen");
    }
  }

  const busy = save.isPending || upload.isPending || remove.isPending;

  // #292 (UC-3): Titel des aktiven Entwurfs für den Kontext-Kopf — teilt den Query-Cache
  // mit der LifecycleBar (derselbe Key), kostet also keinen zweiten Request. Nur bei
  // governed Repos abgefragt (direct kennt keine Entwürfe).
  // Zyklus 2 (LOW-H): exakt Entwurfs-Refs (`draft/…`) — ein Versions-Tag im ref-Param ist
  // KEIN Entwurf und bekäme sonst einen falschen Balken. Bedingung synchron zum Balken.
  const draftsForBanner = useRepoDrafts(repo.lifecycle === "governed" && activeRef.startsWith("draft/") ? repoId : undefined);
  const draftTitle = (draftsForBanner.data ?? []).find((d) => d.branch === activeRef)?.title ?? null;

  return (
    <>
      {/* #278: Lifecycle-Leiste — Stand wählen, Entwurf anlegen/einreichen/freigeben.
          Rendert sich selbst NICHT bei `lifecycle: "direct"` (Agent-Workspaces). */}
      <div className="rounded-xl border border-border bg-surface">
        <LifecycleBar
          repo={repo}
          activeRef={activeRef}
          onRefChange={(r) => {
            navigateTo({ ref: r === LIVE_REF ? null : r, file: null });
            discardDraft(); // ein offener Editor gehört zum alten Stand
          }}
        />
        {writeBlocked && (
          <p className="px-4 py-2 text-[12px] text-text-muted" data-testid="write-blocked-hint">
            {writeBlocked}
          </p>
        )}
        {/* #292 (UC-3): Kontext-Kopf — auf einem Entwurfs-Stand ist NICHTS davon live.
            Das Ref-Dropdown allein ist zu leise; nach ein paar Klicks im Baum vergisst
            man, auf welchem Stand man arbeitet. NUR bei governed (Review LOW-8): bei
            direct-Repos gibt es keine Entwürfe — dort wäre der Satz schlicht falsch. */}
        {repo.lifecycle === "governed" && activeRef.startsWith("draft/") && (
          <p
            className="border-t border-warn-border bg-warn-bg px-4 py-2 text-[12.5px] font-medium"
            data-testid="draft-context-banner"
          >
            Du arbeitest auf dem Entwurf „{draftTitle ?? activeRef}" — nichts davon ist live,
            bis er freigegeben wird.
          </p>
        )}
      </div>

      {/* Kopf: Repo-Info + Breadcrumb + Ordner-Aktionen */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2">
        <Badge variant="outline">{KIND_LABEL[repo.kind]}</Badge>
        <span className="text-[13px] font-medium text-text">{repo.repoFullName}</span>
        <span className="text-[12px] text-text-muted">@{repo.branch}</span>
        <nav className="ml-2 flex items-center gap-1 text-[13px]" aria-label="Pfad">
          <button className="text-accent hover:underline" onClick={() => navigateTo({ path: null, file: null })}>
            /
          </button>
          {crumbs.map((c) => (
            <span key={c.path} className="flex items-center gap-1">
              <span className="text-text-muted">/</span>
              <button className="text-accent hover:underline" onClick={() => navigateTo({ path: c.path, file: null })}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={startNewFile} disabled={busy || schreibgesperrt} title={schreibTitel} data-testid="new-file">
            <FilePlus2 className="mr-1 h-3.5 w-3.5" /> Neue Datei
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => uploadInput.current?.click()}
            disabled={busy || schreibgesperrt}
            title={schreibTitel}
            data-testid="upload-file"
          >
            {upload.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="mr-1 h-3.5 w-3.5" />
            )}
            Hochladen
          </Button>
          {/* #277: Ordner-Upload — viele Dateien, EIN Commit. Neben dem Einzel-Upload,
              weil beides vorkommt: eine Datei ändern vs. ein Plugin einspielen. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            disabled={busy || schreibgesperrt}
            title={schreibTitel}
            data-testid="bulk-upload"
          >
            <FolderUp className="mr-1 h-3.5 w-3.5" /> Ordner
          </Button>
          <input
            ref={uploadInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onUploadFile(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Verzeichnis-Liste */}
        <div className="w-72 shrink-0 overflow-y-auto rounded-xl border border-border bg-surface p-2">
          {tree.isLoading && <Loader2 className="m-2 h-4 w-4 animate-spin text-text-muted" />}
          {tree.isError && <p className="p-2 text-[13px] text-danger">Konnte Verzeichnis nicht laden.</p>}
          {(tree.data ?? []).map((e) => (
            <button
              key={e.path}
              onClick={() => openEntry(e)}
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] ${
                e.path === filePath ? "bg-accent-surface text-accent" : "text-text hover:bg-surface-hover"
              }`}
              data-testid={`entry-${e.path}`}
            >
              {e.type === "dir" ? (
                <Folder className="h-4 w-4 shrink-0 text-text-muted" />
              ) : (
                <File className="h-4 w-4 shrink-0 text-text-muted" />
              )}
              <span className="min-w-0 flex-1 truncate">{e.name}</span>
              {e.type === "file" && (
                <span className="shrink-0 text-[11px] text-text-muted">{formatBytes(e.size)}</span>
              )}
            </button>
          ))}
          {!tree.isLoading && (tree.data ?? []).length === 0 && (
            <p className="p-2 text-[13px] text-text-muted">Leeres Verzeichnis.</p>
          )}
        </div>

        {/* Datei: Editor (draft) > Preview > Platzhalter */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-surface">
          {draft ? (
            <>
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <Pencil className="h-4 w-4 text-text-muted" />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text">
                  {draft.path} {draft.isNew && <Badge variant="outline">neu</Badge>}
                </span>
                <input
                  value={commitMsg}
                  onChange={(e) => setCommitMsg(e.target.value)}
                  placeholder="Notiz zur Änderung (optional)"
                  className="h-8 w-64 rounded-lg border border-border-strong bg-surface px-2 text-[12.5px] text-text outline-none focus:border-accent-border"
                  data-testid="commit-message"
                />
                <Button size="sm" onClick={() => void onSave()} disabled={busy || schreibgesperrt} title={schreibTitel} data-testid="save-file">
                  {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  {isLive(activeRef) ? "Speichern" : "Im Entwurf speichern"}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDraft(null)} title="Abbrechen">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <textarea
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                spellCheck={false}
                className="min-h-0 flex-1 resize-none bg-transparent p-3 font-mono text-[12.5px] leading-relaxed text-text outline-none"
                data-testid="file-editor"
              />
            </>
          ) : !filePath ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-text-muted">Datei auswählen — oder „Neue Datei".</p>
            </div>
          ) : file.isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
            </div>
          ) : file.isError ? (
            <p className="p-4 text-[13px] text-danger">Konnte Datei nicht laden.</p>
          ) : file.data ? (
            <>
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text">
                  {file.data.path}
                </span>
                <span className="text-[11px] text-text-muted">{formatBytes(file.data.size)}</span>
                {!file.data.isBinary && (
                  <Button variant="outline" size="sm" onClick={startEdit} disabled={busy || schreibgesperrt} title={schreibTitel} data-testid="edit-file">
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Bearbeiten
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void onDownload(file.data.path, file.data.name)}
                  data-testid="download-file"
                >
                  <Download className="mr-1 h-3.5 w-3.5" /> Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy || schreibgesperrt}
                  title={schreibTitel}
                  data-testid="delete-file"
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5 text-danger" />
                </Button>
              </div>
              {file.data.isBinary ? (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-text-muted">
                    Binärdatei — keine Vorschau. Über Download ansehen.
                  </p>
                </div>
              ) : (
                <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-[12.5px] leading-relaxed text-text">
                  {file.data.content}
                </pre>
              )}
            </>
          ) : null}
        </div>
      </div>

      {bulkOpen && (
        <BulkUploadDialog
          open
          onOpenChange={setBulkOpen}
          repoId={repoId}
          currentPath={dirPath}
          /* Auf dem Live-Stand ist Schreiben gesperrt — der Dialog sagt das, statt den
             Nutzer in einen 403 laufen zu lassen. Dieselbe Regel wie die Knöpfe: auf einem
             `direct`-Repo (Agent-Workspace) IST der Live-Stand beschreibbar. */
          ref={writeBlocked === null ? activeRef : null}
          onDone={() => void tree.refetch()}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Datei löschen?"
        description={`"${file.data?.path ?? ""}" wird gelöscht. Der bisherige Stand bleibt im Verlauf erhalten.`}
        confirmText="Löschen"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => void onDelete()}
      />
    </>
  );
}
